<style scoped>
.top--20 {
  top: -5rem;
}
</style>
<template>
  <div class="forms-app">
    <div class="flex">
      <NavigationComponent
        v-if="!embed"
        class="fixed bg-white w-full px-4 border-b border-gray-200 z-10"
        :logo="logo"
        :saveState="saveState"
      />
      <div class="flex max-w-6xl mx-auto py-20 px-4 sm:px-6 lg:px-8">
        <TableOfContents
          v-if="toc && toc.length > 0"
          class="hidden sm:block fixed h-full w-64 overflow-y-auto pr-8"
          :toc="toc"
        />
        <div :class="{ 'sm:ml-64': toc && toc.length > 0 }">
          <div v-if="submitted">
            <h1
              class="text-3xl leading-8 font-semibold text-gray-900 sm:text-3xl sm:leading-9"
            >
              {{ title }}
            </h1>
          </div>
          <div aria-live="polite" aria-atomic="true">
            <template v-if="submitted">
              <p class="text-sm font-medium">
                {{
                  thankYouMessage
                    ? thankYouMessage
                    : $t('form.default_thank_you_message')
                }}
              </p>
            </template>
          </div>
          <div v-if="isClosed">
            <h1
              class="text-3xl leading-8 font-semibold text-gray-900 sm:text-3xl sm:leading-9"
            >
              {{ title }}
            </h1>
            <p>{{ $t('form.cannot_be_completed') }}</p>
            <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-600">
              {{ $t('form.start_time', { start_time: openingDateFormatted }) }}
            </p>
            <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-600">
              {{
                $t('form.closing_time', { closing_time: closingDateFormatted })
              }}
            </p>
          </div>
          <div v-if="!submitted && !isClosed">
            {{ resetCount() }}
            <h1
              class="text-3xl leading-8 font-semibold text-gray-900 sm:text-3xl sm:leading-9"
            >
              {{ title }}
            </h1>
            <p
              v-if="respondent"
              class="mt-1 max-w-2xl text-sm leading-5 text-gray-600"
            >
              {{ $t('form.form_for', { respondent }) }}
            </p>
            <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-600">
              {{ $t('form.fields_with_asterisk_required') }}
            </p>

            <ValidationObserver v-slot="{ validate }">
              <form
                ref="form"
                @submit="(e) => onSubmit(e, validate)"
                id="form"
                method="POST"
                enctype="multipart/form-data"
                novalidate
              >
                <input
                  v-if="csrftoken"
                  type="hidden"
                  name="csrfmiddlewaretoken"
                  :value="csrftoken"
                />
                <div v-for="(field, i) in fields" v-bind:key="field.id">
                  <a
                    v-if="['h2', 'h3', 'h4'].includes(field.element)"
                    :id="field.value"
                    class="block relative invisible top--20 anchor"
                  />
                  <h2
                    v-if="field.element == 'h2'"
                    class="text-2xl leading-6 font-semibold text-gray-900 mt-20 border-b border-gray-200 pb-3 mb-3"
                  >
                    {{ field.value }}
                  </h2>
                  <h3
                    v-if="field.element == 'h3'"
                    class="text-lg leading-6 font-medium text-gray-700 mt-9"
                  >
                    {{ field.value }}
                  </h3>
                  <h4
                    v-if="field.element == 'h4'"
                    class="text-sm leading-5 font-medium text-gray-700 mt-8"
                  >
                    {{ field.value }}
                  </h4>
                  <VueMarkdown
                    v-if="field.element == 'p'"
                    class="mt-1 max-w-2xl text-sm leading-5 text-gray-500 prose max-w-none"
                    :source="field.value"
                  />
                  <div v-if="field.element == 'field'" class="mt-6 sm:mt-5">
                    <FieldComponent
                      v-if="isVisible(field)"
                      :number="i + 1"
                      :field="field"
                      :response="response"
                      :csrftoken="csrftoken"
                      :disabled="preview"
                      @change="onChange"
                    />
                  </div>
                </div>

                <div v-if="!preview" class="mt-8 border-t border-gray-200 pt-5">
                  <button type="submit" class="btn-primary">
                    {{ $t('form.submit') }}
                  </button>
                </div>
              </form>
            </ValidationObserver>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import VueMarkdown from 'vue-markdown-render';
import { ValidationObserver } from 'vee-validate';
import jsonLogic from 'json-logic-js';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

import NavigationComponent from './NavigationComponent';
import TableOfContents from './TableOfContents';
import FieldComponent from './FieldComponent.vue';

let counted = 0;

export default {
  name: 'FormComponent',
  components: {
    VueMarkdown,
    ValidationObserver,
    FieldComponent,
    TableOfContents,
    NavigationComponent,
  },
  data() {
    return {
      saveState: 'idle',
      error: false,
      submitted: false,
      currentResponse: this.response,
    };
  },
  created() {
    this.setLang();
  },
  computed: {
    openingDateFormatted() {
      return format(new Date(this.openingDate), 'dd-MM-yyyy HH:mm', {
        locale: nl,
      });
    },
    closingDateFormatted() {
      return format(new Date(this.closingDate), 'dd-MM-yyyy HH:mm', {
        locale: nl,
      });
    },
  },
  methods: {
    setLang() {
      this.$i18n.locale = this.language?.toLowerCase();
    },
    resetCount() {
      counted = 0;
    },
    async onSubmit(e, validate) {
      const isValid = await validate();

      if (this.embed) {
        e.preventDefault();

        if (isValid) {
          const data = Object.fromEntries(new FormData(e.target));

          const result = await fetch(`${this.baseUrl}/responses/`, {
            method: 'POST',
            body: JSON.stringify({
              form: this.id,
              data: data,
            }),
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.csrftoken,
            },
          });

          if (result.ok) {
            this.submitted = true;
            this.error = false;
          } else {
            this.submitted = false;
            this.error = true;
          }
        }
      }

      if (!isValid) {
        e.preventDefault();

        const invalid = this.$refs.form.querySelector('[aria-invalid="true"]');
        if (invalid) {
          invalid.focus();
        }
      }
    },
    count() {
      counted += 1;
      return counted;
    },
    async onChange(e) {
      this.$set(this.currentResponse, e.target.id, e.target.value);

      if (this.code) {
        this.saveState = 'saving';

        try {
          await fetch('', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.csrftoken,
            },
            body: JSON.stringify({
              [e.target.id]: e.target.value,
            }),
          });
        } catch (e) {
          console.error('Could not save change', e);
          this.saveState = 'error';
          return;
        }

        this.saveState = 'idle';
      }
    },
    isVisible(field) {
      if (!field.conditional) {
        return true;
      }

      if (field.conditional.show) {
        return jsonLogic.apply(
          field.conditional.show.when,
          this.currentResponse,
        );
      }

      if (field.conditional.hide) {
        return !jsonLogic.apply(
          field.conditional.hide.when,
          this.currentResponse,
        );
      }

      return true;
    },
  },
  props: {
    id: Number,
    baseUrl: String,
    toc: Array,
    title: String,
    language: String,
    logo: String,
    fields: Array,
    isClosed: Boolean,
    openingDate: String,
    closingDate: String,
    response: Object,
    respondent: String,
    csrftoken: String,
    code: String,
    preview: Boolean,
    embed: Boolean,
    thankYouMessage: String,
  },
};
</script>
